<script setup lang="ts">
import { BUTTON_POSITION, CHAIN_NAMESPACES, CONFIRMATION_STRATEGY, type ProviderConfig } from "@toruslabs/base-controllers";
import { type AccountAbstractionMultiChainConfig } from "@toruslabs/ethereum-controllers";
import { encodeBase64Url } from "@toruslabs/metadata-helpers";
import { Loader } from "@toruslabs/vue-components/Loader";
import { type AuthOptions, base64toJSON } from "@web3auth/auth";
import BigNumber from "bignumber.js";
import parse from "json-stable-parse";
import log from "loglevel";
import { useI18n } from "petite-vue-i18n";
import URI from "urijs";
import { computed, onBeforeMount, Ref, ref } from "vue";
import { useRouter } from "vue-router";

import { Toast } from "./components/Toast";
import useUtils from "./composables/use-utils";
import { DEFAULT_EVM_STATE, DEFAULT_SOLANA_STATE } from "./controllers/ControllerFactory";
import { useAuthStore } from "./store/authStore";
import { useControllerStore } from "./store/controllerStore";
import { DEFAULT_WHITELABEL, ROUTES } from "./utils/enums";
import { BaseLoginParams } from "./utils/interfaces";
import { fetchDataFromStorageServer, gateUser, isMain, parseAccountAbstractionConfig, parseChains, setThemeColors } from "./utils/utils";

BigNumber.config({ EXPONENTIAL_AT: [-10, 20] });
const useController = useControllerStore();
const router = useRouter();
const { setNamespace } = useUtils();
const whiteLabelEmbedState = computed(() => {
  return { ...DEFAULT_WHITELABEL, ...useController.torusState.EmbedControllerState?.whiteLabel };
});

type TExternalRequest = { method: string; params: unknown[]; platform?: string };

const externalRequest: Ref<TExternalRequest | null> = ref(null);
const externalRequestRedirectUrl = ref("");
const authStore = useAuthStore();

async function tryRedirectToExternalApp() {
  if (externalRequest.value !== null && externalRequestRedirectUrl.value !== "") {
    const finalUrl = new URI(externalRequestRedirectUrl.value);
    const resultHashParams = new URLSearchParams(finalUrl.hash().slice(1));
    try {
      const result = await useController.provider.request(externalRequest.value);
      resultHashParams.append("b64Params", encodeBase64Url(JSON.stringify({ success: true, result })));
    } catch (error) {
      resultHashParams.append("b64Params", encodeBase64Url(JSON.stringify({ success: false, error: (error as Error).message })));
      log.error("rehydrateUser: provider init error", error);
    } finally {
      finalUrl.hash(resultHashParams.toString());
      setTimeout(() => {
        window.location.href = finalUrl.href();
      }, 50);
    }
  }
}

async function rehydrateUser() {
  let sessionId = "";
  let accessToken: string | undefined;
  try {
    const hashParams = new URLSearchParams(decodeURIComponent(window.location.hash.slice(1)));
    const b64Params = hashParams.get("b64Params");
    let initController = true;
    let replaceUrlOnRedirect = true;
    if (b64Params) {
      let baseParams = {} as BaseLoginParams;
      try {
        baseParams = { ...baseParams, ...base64toJSON<BaseLoginParams>(b64Params) };
      } catch (error) {
        log.error("invalid base64", error);
        throw new Error(`hash param: ${b64Params} is not a valid base64 string`);
      }

      const { sessionNamespace, loginId, request, platform = null } = baseParams;
      if (sessionNamespace) setNamespace(sessionNamespace);
      if (loginId) {
        const loginConfig = await fetchDataFromStorageServer<string>(loginId, sessionNamespace);
        log.info("login config", loginConfig, parse(loginConfig));
        const { options } = parse(loginConfig) as {
          options: AuthOptions & { accountAbstractionConfig?: AccountAbstractionMultiChainConfig };
        };
        const gatingResponse = await gateUser(options);

        if (!gatingResponse.allowWalletService) {
          throw new Error(gatingResponse.errorMessage);
        }

        const parsedAccountAbstractionConfig = options.accountAbstractionConfig
          ? parseAccountAbstractionConfig({
              accountAbstractionConfig: options.accountAbstractionConfig,
            })
          : null;
        useController.setAccountAbstractionConfig(parsedAccountAbstractionConfig);

        if (baseParams.sessionId) {
          initController = false;
          // get initial chain
          // TODO: is this correct?
          const { initialChain, supportedChains } = parseChains(
            options as unknown as {
              chainId: string;
              chains: ProviderConfig[];
            }
          );

          const defaultEmbedState =
            initialChain.chainNamespace === CHAIN_NAMESPACES.SOLANA
              ? DEFAULT_SOLANA_STATE.EmbedControllerState
              : DEFAULT_EVM_STATE.EmbedControllerState;

          useController.setPlatform(platform);
          useController.setChainConfigs(supportedChains);

          await useController.init({
            state: {
              EmbedControllerState: {
                ...defaultEmbedState,
                dappMetadata: { name: options.redirectUrl, icon: "" }, // use redirect url as dapp name for mobile SDK
                web3AuthClientId: options.clientId,
                web3AuthNetwork: options.network,
                authConnectionConfig: options.authConnectionConfig,
                whiteLabel: { ...DEFAULT_WHITELABEL, ...(options.whiteLabel || {}), buttonPosition: BUTTON_POSITION.BOTTOM_LEFT },
                confirmationStrategy: CONFIRMATION_STRATEGY.DEFAULT,
              },
              NetworkControllerState: {
                providerConfig: initialChain,
                chainId: initialChain.chainId,
                properties: {},
              },
            },
            origin: window.location.origin,
          });

          if (options.whiteLabel?.theme) setThemeColors(options.whiteLabel.theme);

          sessionId = baseParams.sessionId;
          // Citadel token for Auth v11 / PnP mobile; SFA omits this intentionally.
          accessToken = baseParams.accessToken;
          // if coming from mobile sdk, don't replace url params
          replaceUrlOnRedirect = false;

          if (request) {
            // hack to recursive parse params
            const { method, params } = parse(request as unknown as string) as TExternalRequest;
            externalRequest.value = {
              method,
              params,
              platform,
            };
            externalRequestRedirectUrl.value = options.redirectUrl;
          }
        }
      }
    }
    await authStore.rehydrate(initController, sessionId, replaceUrlOnRedirect, accessToken);
  } catch (error) {
    log.error("rehydrateUser", error);
  }
}

const { t } = useI18n();

onBeforeMount(async () => {
  router.beforeEach((to, _, next) => {
    const { hideTransfers, hideTopup, hideNftDisplay, hideTokenDisplay, hideSwap, appName } = whiteLabelEmbedState.value;
    if (to.name === ROUTES.CHECKOUT && hideTopup) {
      router.push({ name: ROUTES.HOME });
      return false;
    }
    if (to.name === ROUTES.TRANSFER_TOKEN && hideTransfers) {
      router.push({ name: ROUTES.HOME });
      return false;
    }
    if (to.name === ROUTES.NFT_DETAILS && hideNftDisplay) {
      router.push({ name: ROUTES.HOME });
      return false;
    }
    if (to.name === ROUTES.TOKEN_DETAILS && hideTokenDisplay) {
      router.push({ name: ROUTES.HOME });
      return false;
    }
    if (to.name === ROUTES.SWAP && hideSwap) {
      router.push({ name: ROUTES.HOME });
      return false;
    }
    if (to.meta?.title) {
      window.document.title = `${t(to.meta.title as string)} | ${appName || "Web3Auth"}`;
    }
    return next();
  });
  authStore.setLoaderState(true);
  if (isMain) {
    await rehydrateUser();
    if (externalRequest.value !== null) tryRedirectToExternalApp();
  }
  authStore.setLoaderState(false);
});
</script>

<template>
  <Toast />
  <div v-if="isMain && authStore.loader" class="flex h-screen flex-col items-center justify-center gap-5">
    <Loader />
  </div>
  <!-- Login / Wallet -->
  <router-view v-else />
</template>
